/**
 * @file Helpers for attachments
 */

import { BeethovenDisplayAttributes } from '@padlet/beethoven-client'
import { PostColor } from 'types'

export enum FallbackAttachmentType {
  Audio,
  Video,
  BrokenLink,
  File,
  GoogleDocument,
  Image,
  Link,
  Presentation,
  PrivateDocument,
  Spreadsheet,
  TextDocument,
}

const fallbackAttachmentTypeMapping: Record<FallbackAttachmentType, string> = {
  [FallbackAttachmentType.Audio]: 'audio',
  [FallbackAttachmentType.Video]: 'video',
  [FallbackAttachmentType.BrokenLink]: 'broken-link',
  [FallbackAttachmentType.File]: 'file',
  [FallbackAttachmentType.GoogleDocument]: 'google-document',
  [FallbackAttachmentType.Image]: 'image',
  [FallbackAttachmentType.Link]: 'link',
  [FallbackAttachmentType.Presentation]: 'presentation',
  [FallbackAttachmentType.PrivateDocument]: 'private-document',
  [FallbackAttachmentType.Spreadsheet]: 'spreadsheet',
  [FallbackAttachmentType.TextDocument]: 'text-document',
}

export const fallbackAttachmentSizeMapping: Record<FallbackAttachmentType, { width; height }> = {
  [FallbackAttachmentType.Audio]: { width: 1032, height: 1032 },
  [FallbackAttachmentType.Video]: { width: 1032, height: 1032 },
  [FallbackAttachmentType.BrokenLink]: { width: 1032, height: 516 },
  [FallbackAttachmentType.File]: { width: 1032, height: 556.248 },
  [FallbackAttachmentType.GoogleDocument]: { width: 1032, height: 516 },
  [FallbackAttachmentType.Image]: { width: 1032, height: 516 },
  [FallbackAttachmentType.Link]: { width: 1032, height: 516 },
  [FallbackAttachmentType.Presentation]: { width: 1032, height: 516 },
  [FallbackAttachmentType.PrivateDocument]: { width: 1032, height: 516 },
  [FallbackAttachmentType.Spreadsheet]: { width: 1032, height: 516 },
  [FallbackAttachmentType.TextDocument]: { width: 1032, height: 516 },
}
/**
 * Proxy through Cloudflare worker which adds appropriate headers so that
 * file at `url` is downloaded instead of opened in the browser.
 **/
export function downloadUrl(url: string): string {
  const encodedUrl: string = encodeURIComponent(url)
  return `https://padletcdn.com/cgi/fetch?disposition=attachment&url=${encodedUrl}`
}

// When the browser can't render an opened attachment, it will download the file, we should use downloadUrl to preserve the original file name when it contains unicode characters
export function urlForOpening(linkData: BeethovenDisplayAttributes | null, displayUrl?: string): string {
  if (!displayUrl) {
    return ''
  }

  if (!linkData) {
    return displayUrl
  }

  const isFile = linkData.content_category === 'file'
  const isDocument = linkData.content_category === 'document'
  const isUpload = linkData.provider_name.toLowerCase() === 'padlet drive'

  if ((isFile || isDocument) && isUpload && !linkData.embed_code) {
    return downloadUrl(displayUrl)
  }

  return displayUrl
}

export function getStylizedProviderName(name: string | null | undefined): string {
  if (!name) return ''
  // This is done to comply with Youtube's requirements in order to use their player on Padlet.
  if (name.toLowerCase() === 'youtube') return name
  return name.toLowerCase()
}

export function fallbackImageUrl(
  type: FallbackAttachmentType,
  colorScheme: 'light' | 'dark',
  color: PostColor,
): string {
  const typeString = fallbackAttachmentTypeMapping[type]
  const colorString = color || 'gray'
  const url = `https://padlet.net/beethoven/fallback/${typeString}/${colorScheme}-${colorString}.png`
  return url
}
